import { useMemo, useState } from 'react';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { InfoDot, NumInput, MoneyInput } from './MortgagePage';
import {
  num, spaLegalFee, motStampDuty, loanLegalFee, loanStampDuty,
  monthlyInstallment, DISBURSEMENT_ESTIMATE,
} from '../lib/loanCalc';

// Compare — two loans side by side, recomputed live as inputs change (no
// Calculate button, matching the reference tool). The entered amount is treated
// as both the property price and the loan (100%-financing comparison), so the
// statutory cash-required lines use the same SRO 2023 / stamp-duty scales as the
// Mortgage tool. Differences are computed on unrounded values.

const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const calcSide = (loan, ratePct, years) => {
  const L = num(loan);
  if (L <= 0) return null;
  const items = {
    spaLegal: spaLegalFee(L),
    spaDisb: DISBURSEMENT_ESTIMATE,
    spaStamp: motStampDuty(L),
    loanLegal: loanLegalFee(L),
    loanDisb: DISBURSEMENT_ESTIMATE,
    loanStamp: loanStampDuty(L),
  };
  const cash = Object.values(items).reduce((s, v) => s + v, 0);
  const installment = monthlyInstallment(L, ratePct, years);
  const totalPayment = installment * Math.round(num(years) * 12);
  return { ...items, cash, installment, totalPayment, interest: Math.max(0, totalPayment - L) };
};

// "Loan #1 paid RM10.36 less than Loan #2" — sign-aware bullet line.
const diffLine = (label, verb, a, b) => {
  const d = a - b;
  return `${label}: Loan #1 ${verb} RM${fmt2(Math.abs(d))} ${d < 0 ? 'less' : 'more'} than Loan #2.`;
};

export default function ComparePage() {
  const [a, setA] = useState({ loan: '', rate: '3.95', years: '30' });
  const [b, setB] = useState({ loan: '', rate: '4.00', years: '30' });

  const ra = useMemo(() => calcSide(a.loan, a.rate, a.years), [a]);
  const rb = useMemo(() => calcSide(b.loan, b.rate, b.years), [b]);
  const both = ra && rb;

  const [infoOpen, setInfoOpen] = useState(false);
  const info = () => setInfoOpen(true);
  const INFO_PAGES = [
    { body: 'Compare 2 loans side by side \u2014 just update the input boxes with your preferred numbers, it will calculate and summarize the results automatically. View the differences in interest to find your best option.' },
    { title: 'Basis', body: 'The entered amount is treated as both the property price and the loan (100% financing comparison). Legal fees per SRO 2023 (minimum RM500); stamp duty per current MOT/loan tiers; RM2,500 estimated per side for disbursements & misc charges.' },
  ];

  const CASH_ROWS = [
    ['spaLegal', 'SPA Legal Fees'], ['spaDisb', 'Disbursement/ Fees*'], ['spaStamp', 'SPA Stamp Duty'],
    ['loanLegal', 'Loan Legal Fees'], ['loanDisb', 'Disbursement/ Fees*'], ['loanStamp', 'Loan Stamp Duty'],
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Compare" right={<InfoDot onClick={info} />} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* inputs — two columns */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <GridRow label="Loan" a={<b>#1</b>} b={<b>#2</b>} />
          <GridRow label="Loan (RM)"
            a={<MoneyInput value={a.loan} onChange={(v) => setA((s) => ({ ...s, loan: v }))} placeholder="0" />}
            b={<MoneyInput value={b.loan} onChange={(v) => setB((s) => ({ ...s, loan: v }))} placeholder="0" />} />
          <GridRow label="Rate (%)"
            a={<NumInput value={a.rate} onChange={(v) => setA((s) => ({ ...s, rate: v }))} placeholder="4.00" />}
            b={<NumInput value={b.rate} onChange={(v) => setB((s) => ({ ...s, rate: v }))} placeholder="4.00" />} />
          <GridRow label="Tenure (Year)"
            a={<NumInput value={a.years} onChange={(v) => setA((s) => ({ ...s, years: v }))} placeholder="30" />}
            b={<NumInput value={b.years} onChange={(v) => setB((s) => ({ ...s, years: v }))} placeholder="30" />} />
        </div>

        {both && (
          <>
            <Block title="CASH REQUIRED">
              {CASH_ROWS.map(([k, label], i) => (
                <GridValueRow key={k + i} label={label} a={fmt2(ra[k])} b={fmt2(rb[k])} />
              ))}
              <GridValueRow label="Total (RM)" a={fmt2(ra.cash)} b={fmt2(rb.cash)} strong />
            </Block>

            <Block title="SUMMARY">
              <GridValueRow label="Interest Payable" a={fmt2(ra.interest)} b={fmt2(rb.interest)} />
              <GridValueRow label="Total Payment" a={fmt2(ra.totalPayment)} b={fmt2(rb.totalPayment)} />
              <GridValueRow label="Instalment (RM)" a={fmt2(ra.installment)} b={fmt2(rb.installment)} strong />
            </Block>

            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.7, padding: '0 4px' }}>
              • {diffLine('Interest Payable', 'paid', ra.interest, rb.interest)}<br />
              • {diffLine('Total Payment', 'paid', ra.totalPayment, rb.totalPayment)}<br />
              • {diffLine('Monthly Instalment', 'pays', ra.installment, rb.installment)}<br />
              • Cash Required: Loan #1 is RM{fmt2(Math.abs(ra.cash - rb.cash))} {ra.cash < rb.cash ? 'less' : 'more'} than Loan #2.
            </div>
          </>
        )}
        {!both && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>
            Enter both loan amounts — results update automatically.
          </div>
        )}
      </div>

      <InfoNotes open={infoOpen} onClose={() => setInfoOpen(false)} title="Compare" pages={INFO_PAGES} />
    </div>
  );
}

const GridRow = ({ label, a, b }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, alignItems: 'center' }}>
    <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
    <div style={{ textAlign: 'center' }}>{a}</div>
    <div style={{ textAlign: 'center' }}>{b}</div>
  </div>
);

const GridValueRow = ({ label, a, b, strong }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 12, padding: '9px 14px', borderTop: '1px solid var(--border)', fontSize: 12.5, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ color: strong ? 'var(--primary)' : 'var(--muted)', fontWeight: strong ? 600 : 400 }}>{label}</span>
    <b style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a}</b>
    <b style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{b}</b>
  </div>
);

const Block = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
    <div style={{ padding: '10px 14px 6px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--primary)' }}>{title}</div>
    {children}
  </div>
);
